import React, { useState, useEffect, useMemo, useRef } from "react";
import MetaTags from 'react-meta-tags';
import { Container, Row, Col, Card, CardBody, Button, Modal, Badge } from "reactstrap";
import { APPOINTMENT_URL, PATIENT_URL, CHAIRS_URL } from "helpers/url_helper";
import { get, post, put, del } from "helpers/api_helper";
import { showErroeAlert, showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import "flatpickr/dist/themes/material_blue.css";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { encodePatientId } from "helpers/url_crypto_helper";
import cookieHelper from "helpers/getCookieData";
import { getLoggedInDoctorOption, getLoggedInDoctorCode, getUserRole } from "helpers/permissionHelper";
import moment from "moment";
import {
  RiCalendarEventLine,
  RiCalendarCheckLine,
  RiTimeLine,
  RiUserHeartLine,
  RiStethoscopeLine,
  RiAddCircleLine,
  RiHeartPulseLine,
  RiUserReceivedLine,
  RiCheckLine,
  RiCloseLine,
  RiEditLine,
  RiEyeLine,
  RiWhatsappLine,
  RiListCheck,
  RiLayoutGridLine,
  RiHospitalLine,
  RiSearchLine,
  RiFilter3Line,
  RiMedicineBottleLine,
  RiDeleteBinLine
} from "react-icons/ri";

import 'bootstrap/dist/css/bootstrap.css';
import '@fullcalendar/react/dist/vdom';
import '@fullcalendar/daygrid/main.min.css';
import '@fullcalendar/timegrid/main.min.css';
import './appointment.scss';

const Appointment = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;

  const [activeView, setActiveView] = useState('calendar'); // 'calendar' or 'list'
  const isDoctor = getUserRole() === 'doctor';
  const calendarRef = useRef(null);
  const currentRangeRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [appointment, setAppointment] = useState({});
  const [DDoptions, setDDOptions] = useState({ patients: [], doctors: [], treatments: [], chairs: [] });
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Table filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [doctorFilter, setDoctorFilter] = useState('all');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("action") === "book" || searchParams.get("action") === "add" || searchParams.get("book") === "true") {
      const defaultDoc = getLoggedInDoctorCode(DDoptions?.doctors || []);
      setAppointment({
        appointment_date: moment().set({ hour: 10, minute: 0 }).toISOString(),
        doctor_code: defaultDoc || null
      });
      setModalOpen(true);
    }
  }, [location.search, DDoptions.doctors]);

  // Load Dropdown Options
  const fetchOptions = async () => {
    try {
      const { success, body } = await get(`${PATIENT_URL}/options`);
      if (success) {
        setDDOptions(body);
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  // Load Appointments for Selected Date / Date Range
  const fetchAppointments = async (target) => {
    setLoading(true);
    try {
      const clinicId = clinic?.id || '8';
      let query = `clinic_id=${clinicId}&flag=c`;

      if (target?.start && target?.end) {
        query += `&start_date=${target.start}&end_date=${target.end}`;
      } else if (target?.date) {
        query += `&_date=${target.date}`;
      } else if (typeof target === 'string') {
        query += `&_date=${target}`;
      } else if (currentRangeRef.current?.start && currentRangeRef.current?.end) {
        query += `&start_date=${currentRangeRef.current.start}&end_date=${currentRangeRef.current.end}`;
      } else {
        query += `&_date=${selectedDate}`;
      }

      const res = await get(`${APPOINTMENT_URL}/todo?${query}`);
      if (res?.success || Array.isArray(res)) {
        const items = res?.body || (Array.isArray(res) ? res : []);
        setAppointmentsList(items);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
    setLoading(false);
  };

  const refreshAppointments = () => {
    if (activeView === 'calendar' && currentRangeRef.current) {
      fetchAppointments(currentRangeRef.current);
    } else {
      fetchAppointments({ date: selectedDate });
    }
  };

  const handleDatesSet = (dateInfo) => {
    const start = moment(dateInfo.start).format('YYYY-MM-DD');
    const end = moment(dateInfo.end).format('YYYY-MM-DD');
    const range = { start, end };
    currentRangeRef.current = range;

    const selMoment = moment(selectedDate);
    const startMoment = moment(dateInfo.start);
    const endMoment = moment(dateInfo.end);
    if (selMoment.isBefore(startMoment) || selMoment.isSameOrAfter(endMoment)) {
      if (dateInfo.view?.currentStart) {
        setSelectedDate(moment(dateInfo.view.currentStart).format('YYYY-MM-DD'));
      }
    }

    fetchAppointments(range);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const formatted = moment(date).format('YYYY-MM-DD');
    setSelectedDate(formatted);
    if (activeView === 'calendar' && calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.gotoDate(date);
    } else if (activeView === 'list') {
      fetchAppointments({ date: formatted });
    }
  };

  const handleTodaySelect = () => {
    const today = moment().format('YYYY-MM-DD');
    setSelectedDate(today);
    if (activeView === 'calendar' && calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.today();
    } else if (activeView === 'list') {
      fetchAppointments({ date: today });
    }
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === 'list') {
      fetchAppointments({ date: selectedDate });
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (activeView === 'list') {
      fetchAppointments({ date: selectedDate });
    }
  }, [selectedDate, activeView]);

  // KPI Calculations
  const kpiStats = useMemo(() => {
    const total = appointmentsList.length;
    const cancelled = appointmentsList.filter(a => a.is_visited == 2 || (Boolean(a.canceled_at) && a.canceled_at !== 'Invalid date')).length;
    const completed = appointmentsList.filter(a => a.completed_time && a.is_visited != 2).length;
    const inTreatment = appointmentsList.filter(a => a.attened_time && !a.completed_time && a.is_visited != 2).length;
    const checkedIn = appointmentsList.filter(a => a.arravel_time && !a.attened_time && !a.completed_time && a.is_visited != 2).length;
    const booked = appointmentsList.filter(a => !a.arravel_time && !a.attened_time && !a.completed_time && a.is_visited != 2).length;

    return { total, checkedIn, inTreatment, completed, booked, cancelled };
  }, [appointmentsList]);

  // Filtered Appointments for List View
  const filteredAppointments = useMemo(() => {
    return appointmentsList.filter(item => {
      const patientName = `${item.patient?.first_name || ''} ${item.patient?.last_name || ''}`.toLowerCase();
      const caseNo = (item.patient?.case_no || '').toLowerCase();
      const mobile = (item.patient?.mobile || '');
      const query = searchQuery.toLowerCase();

      const matchesSearch = !query || patientName.includes(query) || caseNo.includes(query) || mobile.includes(query);

      const isCancelled = item.is_visited == 2 || (Boolean(item.canceled_at) && item.canceled_at !== 'Invalid date');
      const isCompleted = Boolean(item.completed_time) && !isCancelled;
      const isInTreatment = Boolean(item.attened_time && !item.completed_time) && !isCancelled;
      const isCheckedIn = Boolean(item.arravel_time && !item.attened_time) && !isCancelled;
      const isBooked = !item.arravel_time && !item.attened_time && !item.completed_time && !isCancelled;

      let matchesStatus = true;
      if (statusFilter === 'booked') matchesStatus = isBooked;
      if (statusFilter === 'checkedIn') matchesStatus = isCheckedIn;
      if (statusFilter === 'inTreatment') matchesStatus = isInTreatment;
      if (statusFilter === 'completed') matchesStatus = isCompleted;
      if (statusFilter === 'cancelled') matchesStatus = isCancelled;

      const matchesDoctor = doctorFilter === 'all' || item.doctor_code == doctorFilter || item.doctor?.code == doctorFilter || item.doctor?.id == doctorFilter;

      return matchesSearch && matchesStatus && matchesDoctor;
    });
  }, [appointmentsList, searchQuery, statusFilter, doctorFilter]);

  // Calendar Events Format
  const calendarEvents = useMemo(() => {
    return appointmentsList.map(item => {
      const patientName = `${item.patient?.first_name || ''} ${item.patient?.last_name || ''}`.trim() || 'Patient';
      const doc = (DDoptions.doctors || []).find(d => d.value == item.doctor_code || d.value == item.doctor?.code);
      const docColor = doc?.color_code || '#F8CA15';

      const isCancelled = item.is_visited == 2 || (Boolean(item.canceled_at) && item.canceled_at !== 'Invalid date');
      const isCompleted = Boolean(item.completed_time) && !isCancelled;
      const isInTreatment = Boolean(item.attened_time && !item.completed_time) && !isCancelled;
      const isCheckedIn = Boolean(item.arravel_time && !item.attened_time) && !isCancelled;

      let statusPrefix = '📅';
      let bgColor = docColor;
      if (isCancelled) {
        statusPrefix = '❌';
        bgColor = '#64748b';
      } else if (isInTreatment) {
        statusPrefix = '⚡';
        bgColor = '#ef4444';
      } else if (isCheckedIn) {
        statusPrefix = '🔵';
        bgColor = '#0ea5e9';
      } else if (isCompleted) {
        statusPrefix = '✅';
        bgColor = '#10b981';
      }

      return {
        id: String(item.id),
        title: isCancelled ? `[Cancelled] ${patientName}` : patientName,
        start: item.appointment_date,
        backgroundColor: bgColor,
        borderColor: bgColor,
        extendedProps: item
      };
    });
  }, [appointmentsList, DDoptions.doctors]);

  // Progressive Clinical Workflow Handlers
  const handlePatientIn = async (item) => {
    const nowIso = new Date().toISOString();
    const payload = {
      id: item.id,
      arravel_time: nowIso,
      is_visited: 3
    };
    const res = await put(`${APPOINTMENT_URL}/${item.id}`, payload);
    if (res?.success) {
      showSuccessAlert('Patient marked as Arrived (In)!');
      refreshAppointments();
    } else {
      showErroeAlert('Failed to update arrival time.');
    }
  };

  const handleStartTreatment = async (item) => {
    const nowIso = new Date().toISOString();
    const payload = {
      id: item.id,
      attened_time: nowIso,
      is_visited: 1
    };
    const res = await put(`${APPOINTMENT_URL}/${item.id}`, payload);
    if (res?.success) {
      showSuccessAlert('Treatment started! Redirecting to Patient EMR...');
      const encId = encodePatientId(item.patient_id);
      history.push(`/patient?_p=${encId}&_t=Treatment&_appt=${item.id}`);
    } else {
      showErroeAlert('Failed to start treatment.');
    }
  };

  const handleOpenBookingModal = (item = null) => {
    if (item) {
      setAppointment({
        id: item.id,
        patient_id: item.patient_id,
        doctor_code: item.doctor_code || item.doctor?.code,
        chair_code: item.chair_code,
        treatment_code: item.tretment_code,
        appointment_date: item.appointment_date,
        notes: item.notes || '',
        notification_for_patient: item.notification_for_patient || false,
        notification_for_doctor: item.notification_for_doctor || false,
      });
    } else {
      const defaultDoc = getLoggedInDoctorCode(DDoptions?.doctors || []);
      setAppointment({
        appointment_date: moment(`${selectedDate} 10:00`, 'YYYY-MM-DD HH:mm').toISOString(),
        doctor_code: defaultDoc || '',
        notification_for_patient: true,
        notification_for_doctor: true
      });
    }
    setModalOpen(true);
  };

  const handleSaveAppointment = async () => {
    const effectiveDoctorCode = appointment?.doctor_code || (!appointment?.id ? getLoggedInDoctorCode(DDoptions?.doctors || []) : '');

    if (!appointment?.patient_id || !effectiveDoctorCode) {
      showErroeAlert('Please select Patient and Attending Doctor!');
      return;
    }

    if (!appointment?.id && moment(appointment?.appointment_date).add(30, 'minutes').isBefore(moment())) {
      showErroeAlert('Cannot book an appointment for a past time slot! Please select an upcoming slot or future date.');
      return;
    }

    const payload = {
      ...appointment,
      doctor_code: effectiveDoctorCode,
      clinic_id: clinic?.id || '8'
    };

    const res = appointment?.id
      ? await put(`${APPOINTMENT_URL}/${appointment.id}`, payload)
      : await post(APPOINTMENT_URL, payload);

    if (res?.success) {
      showSuccessAlert(appointment?.id ? 'Appointment updated successfully!' : 'Appointment booked successfully!');
      setModalOpen(false);
      setAppointment({});
      refreshAppointments();
    } else {
      showErroeAlert('Unable to save appointment. Please check details.');
    }
  };

  const handleCancelAppointment = async (id) => {
    const { isConfirmed } = await showConfirmAlert(
      'Cancel Appointment',
      'Are you sure you want to cancel this appointment?',
      'Yes, Cancel',
      'Cancelled',
      'Appointment cancelled successfully.'
    );
    if (isConfirmed) {
      const res = await post(`${APPOINTMENT_URL}/cancel`, { id, canceled_at: new Date() });
      if (res?.success) {
        refreshAppointments();
        setModalOpen(false);
      }
    }
  };

  const handleEventClick = (clickInfo) => {
    const rawProps = clickInfo.event.extendedProps;
    handleOpenBookingModal(rawProps);
  };

  return (
    <React.Fragment>
      <div className="page-content appointment-page-container">
        <MetaTags>
          <title>Appointments & Clinical Schedule | {process.env.REACT_APP_TITLE || 'Dental OPD'}</title>
        </MetaTags>

        <Container fluid>
          {/* Header Bar */}
          <div className="page-header-card d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2">
                <div className="p-2 bg-primary-subtle text-primary rounded-3 font-size-18">
                  <RiCalendarCheckLine />
                </div>
                <div>
                  <h4 className="mb-0 font-weight-bold text-slate-800">{t("Appointments & Schedules") || "Appointments & Clinical Schedule"}</h4>
                  <p className="text-muted font-size-12 mb-0">{t("Manage patient bookings, chair allocations, and clinical queues") || "Manage patient bookings, chair allocations, and clinical queues"}</p>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Date Filter */}
              <div className="d-flex align-items-center bg-light border rounded px-2.5 py-1">
                <RiCalendarEventLine className="text-muted me-1.5" />
                <Flatpickr
                  className="form-control form-control-sm border-0 bg-transparent p-0 font-weight-semibold"
                  style={{ width: '115px' }}
                  value={selectedDate}
                  onChange={([date]) => {
                    if (date) handleDateSelect(date);
                  }}
                  options={{ dateFormat: 'Y-m-d', altInput: true, altFormat: 'd M Y' }}
                />
              </div>

              <Button
                color="light"
                size="sm"
                className="border font-weight-semibold"
                onClick={handleTodaySelect}
              >
                {t("Today") || "Today"}
              </Button>

              {/* View Switcher Pill */}
              <div className="view-switcher-pill">
                <button
                  type="button"
                  className={`view-btn ${activeView === 'calendar' ? 'active' : ''}`}
                  onClick={() => handleViewChange('calendar')}
                >
                  <RiCalendarEventLine /> {t("Calendar View")}
                </button>
                <button
                  type="button"
                  className={`view-btn ${activeView === 'list' ? 'active' : ''}`}
                  onClick={() => handleViewChange('list')}
                >
                  <RiListCheck /> {t("List View")}
                </button>
              </div>

              {/* Operatory Chairs Shortcut */}
              <Link to="/chairs" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 font-weight-semibold">
                <RiLayoutGridLine /> {t("Operatory Schedule")}
              </Link>

              {/* New Appointment Button */}
              <Button
                color="primary"
                size="sm"
                className="d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
                onClick={() => handleOpenBookingModal()}
              >
                <RiAddCircleLine /> {t("Book Appointment")}
              </Button>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <Row className="g-3 mb-4">
            <Col sm={6} md={3}>
              <div className="kpi-stat-card">
                <div className="kpi-icon-wrap bg-primary-subtle text-primary">
                  <RiCalendarCheckLine />
                </div>
                <div>
                  <div className="kpi-stat-value">{kpiStats.total}</div>
                  <div className="kpi-stat-label">{t("Scheduled")}</div>
                </div>
              </div>
            </Col>

            <Col sm={6} md={3}>
              <div className="kpi-stat-card">
                <div className="kpi-icon-wrap bg-info-subtle text-info">
                  <RiUserReceivedLine />
                </div>
                <div>
                  <div className="kpi-stat-value text-info">{kpiStats.checkedIn}</div>
                  <div className="kpi-stat-label">{t("Checked-In")}</div>
                </div>
              </div>
            </Col>

            <Col sm={6} md={3}>
              <div className="kpi-stat-card">
                <div className="kpi-icon-wrap bg-danger-subtle text-danger">
                  <RiHeartPulseLine />
                </div>
                <div>
                  <div className="kpi-stat-value text-danger">{kpiStats.inTreatment}</div>
                  <div className="kpi-stat-label">{t("In-Chair")}</div>
                </div>
              </div>
            </Col>

            <Col sm={6} md={3}>
              <div className="kpi-stat-card">
                <div className="kpi-icon-wrap bg-success-subtle text-success">
                  <RiCheckLine />
                </div>
                <div>
                  <div className="kpi-stat-value text-success">{kpiStats.completed}</div>
                  <div className="kpi-stat-label">{t("Completed")}</div>
                </div>
              </div>
            </Col>
          </Row>

          {/* VIEW 1: Full Calendar Mode */}
          {activeView === 'calendar' && (
            <div className="calendar-wrapper-card">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                initialDate={selectedDate}
                datesSet={handleDatesSet}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                }}
                events={calendarEvents}
                eventClick={handleEventClick}
                editable={false}
                selectable={true}
                dayMaxEvents={3}
                weekends={true}
                height="720px"
                nowIndicator={true}
                stickyHeaderDates={true}
                handleWindowResize={true}
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: 'short',
                  hour12: true
                }}
                eventContent={(eventInfo) => {
                  const timeFormatted = eventInfo.event.start ? moment(eventInfo.event.start).format('hh:mm A') : '';
                  const item = eventInfo.event.extendedProps || {};
                  const patientName = `${item.patient?.first_name || ''} ${item.patient?.last_name || ''}`.trim() || eventInfo.event.title;
                  const docObj = (DDoptions.doctors || []).find(d => d.value == item.doctor_code || d.value == item.doctor?.code);
                  const docName = docObj?.label || item.doctor?.name || 'Doctor';
                  const docColor = docObj?.color_code || '#F8CA15';

                  const isCompleted = Boolean(item.completed_time);
                  const isInTreatment = Boolean(item.attened_time && !item.completed_time);
                  const isCheckedIn = Boolean(item.arravel_time && !item.attened_time);

                  let statusBg = isCompleted ? '#ecfdf5' : isInTreatment ? '#fef2f2' : isCheckedIn ? '#f0f9ff' : '#fffbeb';
                  let statusBorder = isCompleted ? '#10b981' : isInTreatment ? '#ef4444' : isCheckedIn ? '#F8CA15' : '#f59e0b';
                  let statusBadge = isCompleted ? '✅' : isInTreatment ? '⚡' : isCheckedIn ? '🔵' : '🟡';

                  return (
                    <div
                      className="d-flex align-items-center gap-1.5 px-2 py-1 text-truncate w-100 rounded"
                      style={{
                        backgroundColor: statusBg,
                        borderLeft: `4px solid ${statusBorder}`,
                        borderTop: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        borderBottom: '1px solid #e2e8f0',
                        color: '#0f172a',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <span
                        className="font-monospace font-weight-bold px-1.5 py-0.5 rounded text-white"
                        style={{ fontSize: '10px', backgroundColor: statusBorder }}
                      >
                        {timeFormatted}
                      </span>
                      <span className="font-weight-bold text-truncate font-size-11" style={{ color: '#0f172a' }}>
                        {statusBadge} {patientName}
                      </span>
                      <span className="text-muted font-size-10 text-truncate ms-auto" style={{ fontWeight: 600 }}>
                        ({docName})
                      </span>
                    </div>
                  );
                }}
              />
            </div>
          )}

          {/* VIEW 2: Interactive Datatable List Mode */}
          {activeView === 'list' && (
            <div className="table-list-wrapper">
              {/* Search & Filter Strip */}
              <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '380px' }}>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white border-end-0 text-muted">
                      <RiSearchLine />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search patient, phone, case #..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {/* Status Filter */}
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '170px' }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses ({kpiStats.total})</option>
                    <option value="booked">Booked ({kpiStats.booked})</option>
                    <option value="checkedIn">Checked In ({kpiStats.checkedIn})</option>
                    <option value="inTreatment">In Treatment ({kpiStats.inTreatment})</option>
                    <option value="completed">Completed ({kpiStats.completed})</option>
                    <option value="cancelled">❌ Cancelled ({kpiStats.cancelled})</option>
                  </select>

                  {/* Doctor Filter */}
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '170px' }}
                    value={doctorFilter}
                    onChange={(e) => setDoctorFilter(e.target.value)}
                  >
                    <option value="all">All Doctors</option>
                    {(DDoptions.doctors || []).map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {filteredAppointments.length === 0 ? (
                <div className="text-center py-5">
                  <RiCalendarEventLine className="text-muted font-size-40 mb-2" />
                  <h6 className="text-muted font-weight-bold">No Appointments Found</h6>
                  <p className="text-muted font-size-12 mb-0">No bookings match the selected date and filters.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-hover">
                    <thead>
                      <tr>
                        <th>Patient Details</th>
                        <th>Date & Time</th>
                        <th>Operatory Chair</th>
                        <th>Attending Doctor</th>
                        <th>Treatment Purpose</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAppointments.map((item) => {
                        const patientName = `${item.patient?.first_name || ''} ${item.patient?.last_name || ''}`.trim() || 'Patient';
                        const docObj = (DDoptions.doctors || []).find(d => d.value == item.doctor_code || d.value == item.doctor?.code);
                        const docName = docObj?.label || item.doctor?.name || 'Doctor';
                        const treatObj = (DDoptions.treatments || []).find(t => t.value == item.tretment_code);
                        const treatName = treatObj?.label || item.tretment_code || 'General Visit';

                        const isCancelled = item.is_visited == 2 || Boolean(item.canceled_at);
                        const isCompleted = Boolean(item.completed_time) && !isCancelled;
                        const isInTreatment = Boolean(item.attened_time && !item.completed_time) && !isCancelled;
                        const isCheckedIn = Boolean(item.arravel_time && !item.attened_time) && !isCancelled;
                        const isBooked = !item.arravel_time && !item.attened_time && !item.completed_time && !isCancelled;

                        return (
                          <tr key={item.id} className={isCancelled ? 'opacity-75 bg-light' : ''}>
                            <td>
                              <Link
                                to={`/patient?_p=${encodePatientId(item.patient_id)}`}
                                className="d-flex align-items-center gap-2 text-dark font-weight-semibold"
                              >
                                <div className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold font-size-11">
                                  {patientName.substring(0, 2).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-weight-semibold">{patientName}</div>
                                  <div className="text-muted font-size-11">
                                    Case: {item.patient?.case_no || `#${item.patient_id}`} • {item.patient?.mobile || '-'}
                                  </div>
                                </div>
                              </Link>
                            </td>

                            <td>
                              <div className="font-weight-semibold text-slate-800">
                                {moment(item.appointment_date).format('DD MMM YYYY')}
                              </div>
                              <div className="text-primary font-size-12 d-flex align-items-center gap-1 font-weight-medium">
                                <RiTimeLine /> {moment(item.appointment_date).format('hh:mm A')}
                              </div>
                            </td>

                            <td>
                              <span className="badge bg-light text-dark border font-size-12 px-2 py-1">
                                Chair {item.chair_code || '1'}
                              </span>
                            </td>

                            <td>
                              <div className="d-flex align-items-center gap-1.5 font-size-13">
                                <RiStethoscopeLine className="text-primary" />
                                <span className="font-weight-medium">{docName}</span>
                              </div>
                            </td>

                            <td>
                              <div className="font-size-13 text-slate-800 font-weight-medium">
                                {treatName}
                              </div>
                              {item.notes && (
                                <div className="text-muted font-size-11 text-truncate" style={{ maxWidth: 200 }}>
                                  {item.notes}
                                </div>
                              )}
                            </td>

                            <td>
                              {isCancelled ? (
                                <Badge color="danger-subtle" className="text-danger border border-danger font-size-11">
                                  ❌ Cancelled
                                </Badge>
                              ) : isCompleted ? (
                                <Badge color="success-subtle" className="text-success border border-success font-size-11">
                                  ✅ Completed
                                </Badge>
                              ) : isInTreatment ? (
                                <Badge color="danger-subtle" className="text-danger border border-danger font-size-11 d-inline-flex align-items-center gap-1">
                                  <span className="spinner-grow spinner-grow-sm" style={{ width: 6, height: 6 }} /> In Treatment
                                </Badge>
                              ) : isCheckedIn ? (
                                <Badge color="info-subtle" className="text-info border border-info font-size-11">
                                  🔵 Checked In
                                </Badge>
                              ) : (
                                <Badge color="warning-subtle" className="text-warning border border-warning font-size-11">
                                  🟡 Booked
                                </Badge>
                              )}
                            </td>

                            <td className="text-end">
                              <div className="d-inline-flex align-items-center">
                                {/* Progressive Clinical Action Buttons */}
                                {isBooked && (
                                  <Button
                                    size="sm"
                                    color="success"
                                    className="py-1 px-2 font-size-11 d-inline-flex align-items-center gap-1 font-weight-semibold me-1"
                                    onClick={() => handlePatientIn(item)}
                                    title="Mark Patient In"
                                  >
                                    <RiUserReceivedLine /> Patient In
                                  </Button>
                                )}

                                {isCheckedIn && (
                                  <Button
                                    size="sm"
                                    color="primary"
                                    className="py-1 px-2 font-size-11 d-inline-flex align-items-center gap-1 font-weight-semibold me-1"
                                    onClick={() => handleStartTreatment(item)}
                                    title="Start Treatment Session"
                                  >
                                    <RiHeartPulseLine /> Start Treatment
                                  </Button>
                                )}

                                {isInTreatment && (
                                  <Link
                                    to={`/patient?_p=${encodePatientId(item.patient_id)}&_t=Treatment&_appt=${item.id}`}
                                    className="btn btn-sm btn-outline-danger py-1 px-2 font-size-11 d-inline-flex align-items-center gap-1 font-weight-semibold me-1"
                                  >
                                    <RiHeartPulseLine /> Open EMR
                                  </Link>
                                )}

                                <Button
                                  size="sm"
                                  color="light"
                                  className="border py-1 px-1.5 me-1"
                                  onClick={() => handleOpenBookingModal(item)}
                                  title="Edit Booking"
                                >
                                  <RiEditLine />
                                </Button>

                                <Link
                                  to={`/patient?_p=${encodePatientId(item.patient_id)}`}
                                  className="btn btn-sm btn-light border py-1 px-1.5 me-1"
                                  title="Open EMR Chart"
                                >
                                  <RiEyeLine />
                                </Link>

                                {item.patient?.mobile && (
                                  <a
                                    href={`https://wa.me/${item.patient.mobile.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${patientName}, your appointment at 909 Dental OPD is scheduled on ${moment(item.appointment_date).format('DD MMM YYYY, hh:mm A')}.`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-success py-1 px-1.5 me-1"
                                    title="WhatsApp Patient"
                                  >
                                    <RiWhatsappLine />
                                  </a>
                                )}

                                <Button
                                  size="sm"
                                  color="outline-danger"
                                  className="py-1 px-1.5 me-1"
                                  onClick={() => handleCancelAppointment(item.id)}
                                  title="Cancel Appointment"
                                >
                                  <RiDeleteBinLine />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Redesigned Modern Appointment Booking / Edit Modal */}
          <Modal size="lg" isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
            <div className="modal-header border-bottom py-3 px-4">
              <h5 className="modal-title font-weight-bold d-flex align-items-center gap-2">
                <RiCalendarCheckLine className="text-primary" />
                {appointment?.id ? 'Edit / Reschedule Appointment' : 'Book Dental Appointment'}
              </h5>
              <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
            </div>

            <div className="modal-body p-4">
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label font-weight-semibold">{t("Patient Name")} <span className="text-danger">*</span></label>
                  <Select
                    id="patient_select"
                    isClearable={true}
                    isSearchable={true}
                    options={DDoptions?.patients}
                    value={(DDoptions?.patients || []).find(p => p.value == appointment?.patient_id) || null}
                    onChange={(op) => setAppointment(prev => ({ ...prev, patient_id: op?.value || null }))}
                    placeholder={t("Search & Select Patient")}
                  />
                </Col>

                <Col md={6}>
                  <label className="form-label font-weight-semibold">{t("Attending Doctor")} <span className="text-danger">*</span></label>
                  {isDoctor ? (
                    <div className="form-control bg-light d-flex align-items-center gap-2" style={{ cursor: 'not-allowed' }}>
                      <span
                        className="d-inline-block rounded-circle flex-shrink-0"
                        style={{ width: 10, height: 10, backgroundColor: getLoggedInDoctorOption(DDoptions?.doctors || [])?.color_code || '#F8CA15' }}
                      />
                      <span className="font-weight-semibold text-dark">{getLoggedInDoctorOption(DDoptions?.doctors || [])?.label || 'Doctor'}</span>
                    </div>
                  ) : (
                    <Select
                      id="doctor_select"
                      isClearable={true}
                      isSearchable={true}
                      options={DDoptions?.doctors}
                      value={(DDoptions?.doctors || []).find(d =>
                        (appointment?.doctor_code && String(d.value).toLowerCase() === String(appointment.doctor_code).toLowerCase()) ||
                        (appointment?.doctor_code && String(d.label).toLowerCase() === String(appointment.doctor_code).toLowerCase())
                      ) || (!appointment?.id && !appointment?.doctor_code ? getLoggedInDoctorOption(DDoptions?.doctors || []) : null)}
                      onChange={(op) => setAppointment(prev => ({ ...prev, doctor_code: op?.value || null }))}
                      placeholder={t("Select Doctor")}
                      formatOptionLabel={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="d-inline-block rounded-circle flex-shrink-0"
                            style={{ width: 10, height: 10, backgroundColor: option.color_code || '#F8CA15' }}
                          />
                          <span className="font-weight-semibold">{option.label}</span>
                          {option.email && <span className="font-size-11 text-muted ms-auto">{option.email}</span>}
                        </div>
                      )}
                    />
                  )}
                </Col>

                <Col md={6}>
                  <label className="form-label font-weight-semibold">{t("Appointment Date & Time")} <span className="text-danger">*</span></label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      enableTime: true,
                      dateFormat: "Y-m-d H:i",
                      altInput: true,
                      altFormat: "F j, Y h:i K"
                    }}
                    value={appointment?.appointment_date}
                    onChange={([date]) => {
                      if (date) setAppointment(prev => ({ ...prev, appointment_date: date.toISOString() }));
                    }}
                    placeholder="Select Date & Time"
                  />
                </Col>

                <Col md={6}>
                  <label className="form-label font-weight-semibold">{t("Operatory Chair / Cabin")}</label>
                  <Select
                    id="chair_select"
                    isClearable={true}
                    options={DDoptions?.chairs}
                    value={(DDoptions?.chairs || []).find(c => c.value == appointment?.chair_code) || null}
                    onChange={(op) => setAppointment(prev => ({ ...prev, chair_code: op?.value || null }))}
                    placeholder={t("Select Chair")}
                  />
                </Col>

                <Col md={12}>
                  <label className="form-label font-weight-semibold">{t("Treatment / Purpose")}</label>
                  <Select
                    id="treatment_select"
                    isClearable={true}
                    isSearchable={true}
                    options={DDoptions?.treatments}
                    value={(DDoptions?.treatments || []).find(t => t.value == appointment?.treatment_code) || null}
                    onChange={(op) => setAppointment(prev => ({ ...prev, treatment_code: op?.value || null }))}
                    placeholder={t("Select Treatment Procedure")}
                  />
                </Col>

                <Col md={12}>
                  <label className="form-label font-weight-semibold">{t("Clinical Notes / Instructions")}</label>
                  <textarea
                    rows={3}
                    className="form-control"
                    placeholder={t("Enter reason for visit or special clinical instructions...")}
                    value={appointment?.notes || ''}
                    onChange={(e) => setAppointment(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </Col>
              </Row>

              <div className="mt-3 p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between">
                <div>
                  <div className="font-weight-semibold font-size-13 text-slate-800">Automated Patient Reminders</div>
                  <div className="text-muted font-size-11">Send appointment confirmation & reminders via WhatsApp/SMS</div>
                </div>
                <div className="form-check form-switch form-switch-md">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="patientNotifySwitch"
                    checked={appointment?.notification_for_patient || false}
                    onChange={(e) => setAppointment(prev => ({ ...prev, notification_for_patient: e.target.checked }))}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer border-top py-2.5 px-4 d-flex justify-content-between">
              {appointment?.id ? (
                <Button color="outline-danger" size="sm" onClick={() => handleCancelAppointment(appointment.id)}>
                  <RiDeleteBinLine className="me-1" /> Cancel Appointment
                </Button>
              ) : <div />}

              <div className="d-flex align-items-center gap-2">
                <Button color="secondary" size="sm" onClick={() => setModalOpen(false)}>
                  Close
                </Button>
                <Button color="primary" size="sm" className="font-weight-semibold" onClick={handleSaveAppointment}>
                  {appointment?.id ? 'Update Appointment' : 'Confirm & Book'}
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Appointment;
